import React, { useState } from 'react';
import styled from 'styled-components';
import { api } from '../api';
import { authHeader } from '../auth';
import AccessGateModal from '../components/AccessGateModal';
import useAccessGate from '../hooks/useAccessGate';
import { Search, Globe, Briefcase } from 'lucide-react';
import toast from 'react-hot-toast';

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #eef2ff 0%, #e0f2fe 100%);
  padding: 2rem 1rem;
`;

const Container = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;

const PageTitle = styled.h1`
  font-size: 2.2rem;
  font-weight: 800;
  color: #111827;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PageSubtitle = styled.p`
  font-size: 0.95rem;
  color: #4b5563;
  margin-bottom: 2rem;
  max-width: 640px;
`;

const Card = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.08);
  border: 1px solid #e5e7eb;
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.9rem;
  font-weight: 600;
  color: #374151;
  margin-bottom: 0.4rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 140px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 0.75rem 0.9rem;
  font-size: 0.9rem;
  resize: vertical;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #6366f1;
  }
`;

const Input = styled.input`
  width: 100%;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  padding: 0.6rem 1rem;
  font-size: 0.9rem;
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: #6366f1;
  }
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const SearchButton = styled.button`
  margin-top: 1rem;
  background: linear-gradient(135deg, #6366f1, #8b5cf6);
  color: #ffffff;
  border: none;
  border-radius: 999px;
  padding: 0.7rem 1.4rem;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
`;

const JobsList = styled.div`
  margin-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const JobCard = styled.div`
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e5e7eb;
  padding: 1rem 1.25rem;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
`;

const JobTitle = styled.h3`
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.25rem;
`;

const JobMeta = styled.div`
  font-size: 0.85rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
`;

const JobSnippet = styled.p`
  font-size: 0.9rem;
  color: #4b5563;
  margin-bottom: 0.75rem;
`;

const JobLink = styled.a`
  font-size: 0.85rem;
  font-weight: 600;
  color: #4f46e5;
  text-decoration: none;
`;

const GlobalJobs = () => {
  const { gate, requirePremium, closeGate } = useAccessGate();
  const [cvText, setCvText] = useState('');
  const [location, setLocation] = useState('');
  const [loading, setLoading] = useState(false);
  const [jobs, setJobs] = useState([]);

  const handleSearch = async () => {
    const allowed = await requirePremium();
    if (!allowed) return;
    if (!cvText.trim()) {
      toast.error('Önce CV metninizi girin.');
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.post(
        '/api/jobs/global',
        { cvText, location },
        { headers: authHeader() }
      );
      setJobs(data.jobs || []);
      if (!data.jobs || data.jobs.length === 0) {
        toast('Şu an için uygun ilan bulunamadı, kriterleri genişletmeyi deneyin.');
      } else {
        toast.success('Global iş ilanları getirildi.');
      }
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'İlanlar alınırken bir hata oluştu.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer>
      <Container>
        <PageTitle>
          <Globe size={26} />
          Global İş İlanları
        </PageTitle>
        <PageSubtitle>
          CV'nizdeki deneyim ve becerilere göre Jooble üzerinden dünya çapında iş ilanlarını tarayalım.
        </PageSubtitle>

        <Card>
          <Row>
            <div>
              <Label>CV veya Profesyonel Özet</Label>
              <TextArea
                placeholder="CV metninizi veya kariyer özetinizi buraya yapıştırın (Türkçe ya da İngilizce)."
                value={cvText}
                onChange={(e) => setCvText(e.target.value)}
              />
            </div>
            <div>
              <Label>Lokasyon (isteğe bağlı)</Label>
              <Input
                placeholder="Örn: İstanbul, Türkiye veya Remote"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
              />
              <SearchButton type="button" onClick={handleSearch} disabled={loading}>
                <Search size={16} />
                {loading ? 'İlanlar aranıyor...' : 'Uygun iş ilanlarını getir'}
              </SearchButton>
            </div>
          </Row>
        </Card>

        {jobs.length > 0 && (
          <JobsList>
            {jobs.map((job, index) => (
              <JobCard key={job.id || job.link || index}>
                <JobTitle>{job.title}</JobTitle>
                <JobMeta>
                  <Briefcase size={14} style={{ marginRight: 4 }} />
                  {job.company} • {job.location || 'Lokasyon belirtilmemiş'}
                </JobMeta>
                {job.snippet && <JobSnippet>{job.snippet}</JobSnippet>}
                {job.link && (
                  <JobLink href={job.link} target="_blank" rel="noreferrer">
                    İlanı Görüntüle
                  </JobLink>
                )}
              </JobCard>
            ))}
          </JobsList>
        )}
      </Container>
      <AccessGateModal type={gate} onClose={closeGate} />
    </PageContainer>
  );
};

export default GlobalJobs;
